import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React, { FC } from "react";
import { T } from "services/lang/LanguageService";
import {
  allPositionOptions,
  CurrentDateDisplayProps,
  MonthDisplayType,
  monthDisplayTypeOptions,
  TimeType,
  twoPositionOptions,
} from "../CurentDateDisplayTypes";
import getCurrentDateExample from "../CurrentDateDisplayUtils";

const CurrentDateDisplayEditor: FC<DashboardEditorProps<CurrentDateDisplayProps>> = ({
  config,
  onConfigChange,
}) => {
  const numberOfShownItems = [
    config.year?.show !== false,
    config.month?.show !== false,
    config.day?.show !== false,
  ].filter(Boolean).length;

  const positionOptions = numberOfShownItems === 2 ? twoPositionOptions : allPositionOptions;

  const handleShowSwitchChange = (timeType: TimeType, value: boolean) => {
    const updated = { ...config };
    updated[timeType] = { ...updated[timeType], show: value };
    if (!value) {
      updated[timeType].position = undefined;
      if (timeType === TimeType.MONTH) {
        updated[timeType].displayType = undefined;
      }
    }
    onConfigChange(updated);
  };

  const handlePositionChange = (timeType: TimeType) => (e: any) => {
    const newPosition = e.value;
    const updated = { ...config };

    (Object.keys(config) as TimeType[]).forEach(type => {
      if (type !== timeType && config[type].position === newPosition) {
        updated[type] = { ...updated[type], position: undefined };
      }
    });

    updated[timeType] = { ...updated[timeType], position: newPosition };
    onConfigChange(updated);
  };

  return (
    <div className="flex flex-column">
      <div className="flex flex-row gap-3">
        <label>{T("label.current-date-example")}</label>
        <div>{getCurrentDateExample(config.year, config.month, config.day)}</div>
      </div>
      <Divider />

      <div className="flex flex-row gap-3">
        <div className="flex flex-column gap-2 w-2">
          <label>{T("action.show-years")}</label>
          <InputSwitch
            checked={config.year?.show ?? true}
            onChange={e => handleShowSwitchChange(TimeType.YEAR, e.value)}
          />
        </div>
        {config.year?.show !== false && (
          <>
            {numberOfShownItems > 1 && (
              <div className="flex flex-column gap-2 w-2">
                <label>{T("label.year-position")}</label>
                <Dropdown
                  value={config.year?.position}
                  options={positionOptions}
                  onChange={handlePositionChange(TimeType.YEAR)}
                />
              </div>
            )}
            <div className="flex flex-column gap-2 w-2">
              <label htmlFor="suffix">{T("label.year-suffix")}</label>
              <InputText
                id="suffix"
                value={config.year?.suffix}
                onChange={e =>
                  onConfigChange({ ...config, year: { ...config.year, suffix: e.target.value } })
                }
              />
            </div>
          </>
        )}
      </div>
      <Divider />
      <div className="flex flex-row gap-3">
        <div className="flex flex-column gap-2 w-2">
          <label>{T("action.show-months")}</label>
          <InputSwitch
            checked={config.month?.show ?? true}
            onChange={e => handleShowSwitchChange(TimeType.MONTH, e.value)}
          />
        </div>
        {config.month?.show !== false && (
          <>
            {numberOfShownItems > 1 && (
              <div className="flex flex-column gap-2 w-2">
                <label>{T("label.month-position")}</label>
                <Dropdown
                  value={config.month?.position}
                  options={positionOptions}
                  onChange={handlePositionChange(TimeType.MONTH)}
                />
              </div>
            )}
            <div className="flex flex-column gap-2 w-2">
              <label>{T("label.month-display-type")}</label>
              <Dropdown
                value={config.month?.displayType ?? MonthDisplayType.NUMBER}
                options={monthDisplayTypeOptions}
                onChange={e =>
                  onConfigChange({
                    ...config,
                    month: { ...config.month, displayType: e.value },
                  })
                }
              />
            </div>
          </>
        )}
      </div>
      <Divider />

      <div className="flex flex-row gap-3">
        <div className="flex flex-column gap-2 w-2">
          <label>{T("action.show-days")}</label>
          <InputSwitch
            checked={config.day?.show ?? true}
            onChange={e => handleShowSwitchChange(TimeType.DAY, e.value)}
          />
        </div>
        {config.day?.show !== false && numberOfShownItems > 1 && (
          <div className="flex flex-column gap-2 w-2">
            <label>{T("label.day-position")}</label>
            <Dropdown
              value={config.day?.position}
              options={positionOptions}
              onChange={handlePositionChange(TimeType.DAY)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentDateDisplayEditor;
